<script setup lang="ts">
// Vue
import { computed, onBeforeUnmount, onMounted, ref, useTemplateRef } from "vue";

// Dependencies
import { v4 as uuidv4 } from "uuid";
import { VueDatePicker } from "@vuepic/vue-datepicker";
import "@vuepic/vue-datepicker/dist/main.css";

import format from "@/services/Utils/format";

// Components
import InputLabel from "./InputLabel.vue";

const props = defineProps<{
  modelValue?: string | null;
  value?: string | null;
  label?: string;
  color: "primary";
  errorMsg?: string;
  required?: boolean;
  disabled?: boolean;
  // For a field labelled from outside this component (a <label for> of the caller's own).
  inputId?: string;
  // Needed with inputId: the picker's own aria-label overrides a <label for>.
  ariaLabel?: string;
}>();

const emits = defineEmits<{
  (e: "update:modelValue", value: string): void;
  (e: "update:value", value: string): void;
}>();

// modelValue is the primary v-model prop; value is kept as a backwards-compatible alias
const effectiveValue = computed(() => (props.modelValue !== undefined ? props.modelValue : props.value ?? null));

const localId = props.inputId ?? uuidv4();

const pickerValue = computed(() => (effectiveValue.value ? String(format.date(effectiveValue.value)) : null));

// The picker hands back null when cleared; callers have always received "" for an empty native date input.
const onPicked = (picked: string | null) => {
  emits("update:modelValue", picked ?? "");
  emits("update:value", picked ?? "");
};
const placement = "bottom-start" as const;
const minDate = new Date(2000, 0, 1);

const inputAttrs = computed(() => ({
  id: localId,
  required: props.required ?? false,
  clearable: !props.required,
  state: props.errorMsg ? false : undefined,
}));

const shift = { crossAxis: true, padding: 8 };
const flip = { padding: 8 };
const root = useTemplateRef<HTMLElement>("root");
const picker = useTemplateRef("picker");
const scrollParent = ref<HTMLElement | null>(null);
const closeOnScroll = () => picker.value?.closeMenu();

onMounted(() => {
  let parent = root.value?.parentElement;
  while (parent && !/(auto|scroll)/.test(getComputedStyle(parent).overflowY)) parent = parent.parentElement;
  if (parent) scrollParent.value = parent;
});
onBeforeUnmount(() => scrollParent.value?.removeEventListener("scroll", closeOnScroll));
</script>

<template>
  <div
    ref="root"
    class="date-input flex w-full flex-col">
    <InputLabel
      :id="localId"
      :label="label"
      :required="required" />
    <VueDatePicker
      ref="picker"
      :model-value="pickerValue"
      model-type="yyyy-MM-dd"
      :formats="{ input: 'dd/MM/yyyy' }"
      :text-input="{ format: 'dd/MM/yyyy', maskFormat: 'DD/MM/YYYY' }"
      :time-config="{ enableTimePicker: false }"
      :week-start="1"
      :min-date="minDate"
      :year-range="[2000, 2100]"
      :input-attrs="inputAttrs"
      :aria-labels="{ input: ariaLabel ?? label ?? 'Date' }"
      :disabled="disabled"
      :ui="{ input: 'h-9 t-small', menu: 'date-input-menu popover-keep-open min-h-[280px] max-h-[320px] overflow-y-auto' }"
      teleport
      :floating="{ shift, flip, placement }"
      placeholder="dd/mm/yyyy"
      auto-apply
      @update:model-value="onPicked"
      @open="scrollParent?.addEventListener('scroll', closeOnScroll, { passive: true })"
      @closed="scrollParent?.removeEventListener('scroll', closeOnScroll)" />
    <span
      v-if="errorMsg"
      :id="`${localId}-error`"
      class="text-app-red-700 text-xs"
      >{{ errorMsg }}</span
    >
  </div>
</template>

<style>
.date-input .dp--theme-light,
.dp--theme-light.date-input-menu,
.date-input-menu .dp--theme-light {
  --dp-font-family: var(--font-inter);
  --dp-font-size: var(--text-14);
  --dp-border-radius: var(--radius-8);
  --dp-background-color: var(--color-elevated);
  --dp-text-color: var(--color-type-default);
  --dp-icon-color: var(--color-type-muted);
  --dp-border-color: var(--color-slate-300);
  --dp-border-color-hover: var(--color-slate-300);
  --dp-border-color-focus: var(--color-app-blue-600);
  --dp-menu-border-color: var(--color-slate-200);
  --dp-hover-color: var(--color-slate-100);
  --dp-hover-text-color: var(--color-type-default);
  --dp-primary-color: var(--color-app-blue-fill);
  --dp-primary-text-color: var(--color-white);
  --dp-secondary-color: var(--color-type-muted);
  --dp-danger-color: var(--color-app-red-500);
  --dp-disabled-color: var(--color-slate-100);
  --dp-disabled-color-text: var(--color-type-muted);
}

.date-input-menu {
  box-shadow: var(--shadow-dropdown);
}

/* A red border like the other inputs' error state, not the library's red glow. */
.date-input .dp--input.dp--input-invalid {
  border-color: var(--color-app-red-500);
  box-shadow: none;
}
</style>
